import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mailcheck from 'mailcheck';
import debounce from 'lodash.debounce';
import ApiRequest from 'core/api/request';
import { BrazeUtil } from 'core/utils';

const emailErrorMap = {
	invalidFormat: "Invalid email format",
	genericError: "Could not verify email is valid.",
	submittedWithoutProviding: "Please provide a valid email address to place an order."
};

const useGuestEmail = (cartIds=[], defaultValue="") => {
	const [email, setEmail] = useState(defaultValue);
	const [suggestion, setSuggestion] = useState();
	const [validationError, setValidationError] = useState();
	// TODO: use this as a way to say "this doesn't match a probable valid email but push it through if you're sure it's right"
	const [softValidated, setSoftValidated] = useState();
	const formRef = useRef(null);
	const isValidRef = useRef(() => true);
	const firstRenderRef = useRef(true);

	const setGlobalEmail = (email) => {
		if (!window.cj || !window.cj.guest) {
			window.cj.guest = { email: email };
		} else {
			window.cj.guest.email = email;
		}
	};

	const validateEmail = useCallback((formEmail) => {
		const validEmailEntered = (formEmail) => {
			if (suggestion) {
				setSuggestion();
			}
			setValidationError();
			setSoftValidated(true);
			setEmail(formEmail);
			setGlobalEmail(formEmail);
			// e-mail looks good, submit the form and link the cart and email
			formRef.current.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
			// Validate the rest of the checkout components
			// TODO: Delete once we get rid of backbone
			window.cj?.checkoutOnestepRenderValidate &&
			window.cj?.checkoutOnestepRenderValidate();
		};

		if (formEmail === "") {
			// Don't update any state, this is good for when the component first renders
			// to avoid indicating an error before the user has input anything
			return;
		}

		ApiRequest.post('/customer/check_email', { email: formEmail })
			.then(({ isCustomer }) => {
				if (isCustomer) {
					// A customer already exists with this email, no need to validate further
					// just set that its a registered user in the global context
					window.cj.registeredUser = true;
					validEmailEntered(formEmail);
				} else {
					// Email passed regex on api call and does not belong to an existing customer
					// Use mailcheck to verify it looks like a valid email from a common domain
					mailcheck.run({
						formEmail,
						suggested: (suggestion) => {
							setSuggestion(suggestion);
							setValidationError(emailErrorMap.invalidFormat);
							// If we have a suggestion it could maybe still be a valid email
							// so we'll set it as somewhat validated and put the onus on the user
							// to verify before we'll let them check out
							setSoftValidated(true);
							setEmail(formEmail);
							setGlobalEmail(formEmail);
						},
						empty: () => {
							validEmailEntered(formEmail);
							// Unset the global context for registered user, since they aren't
							// using an email that belongs to a user
							window.cj.registeredUser = false;
						},
					});
				}
			})
			.catch(e => {
				const error = e["errors"][0];
				setSoftValidated(false);
				setSuggestion();
				if (error.email) {
					setValidationError(emailErrorMap.invalidFormat);
				} else {
					setValidationError(emailErrorMap.genericError);
				}
			});
	}, [suggestion]);

	useEffect(() => {
		const isFormValid = () => {
			if (!email) {
				// Not a valid form if there's no email
				// so set state as appropriate
				setValidationError(emailErrorMap.submittedWithoutProviding);
				setSoftValidated(false);
				setSuggestion();
				return false;
			} else {
				// We can rely on the validation error state, because
				// if the email field hasn't been interacted with at all
				// we'll return false above, otherwise we'll have run validation
				// on input and the state should be set correctly.
				return !validationError;
			}
		};

		isValidRef.current = isFormValid;

		// TODO: Delete once we get rid of backbone
		// hack to expose the validator to backbone
		window.cj.guestEmail = { isValid: isValidRef.current };
	}, [email, validationError]);

	// only run this validator after no more input has changed for half of a second
	// It's memoized with no dependencies so that we only create the debounce function
	// once on the first render
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleEmailChange = useMemo(() => debounce(email => validateEmail(email), 500), []);

	useEffect(() => {
		if (firstRenderRef.current) {
			// First render, we don't want to validate anything yet
			// but we do want to on subsequent changes.
			firstRenderRef.current = false;
			return;
		}
		handleEmailChange(email);
	}, [email, handleEmailChange]);

	const onEmailSubmit = (e) => {
		e.preventDefault();
		if (!isValidRef.current()) {
			return;
		}

		// Link each of `cartIds` (for multicart)
		cartIds.forEach((cartId) => {
			// Make API call to create cart/email link (fail silently)
			ApiRequest.post(`/cart_emails/cart/${cartId}/link_to_email/`, { email: email })
				.then(({ email, cartId }) => BrazeUtil.addToCartGuest(email, cartId))
				.catch(e => console.error(e));
		});
	};

	// Export stuff we want to share
	const state = {
		validationError,
		email,
		softValidated,
	};

	const helpers = {
		onEmailSubmit,
		formRef,
		setEmail,
	};

	return [state, helpers];
};

export default useGuestEmail;
